// ESTATISTICAS — o MAPA DO MES (25/09/2026, emenda da [DA-759]; a aba nasceu na [DA-692]).
//
// Pedido dele com o mapa do dia aberto: *"Gostaria de acompanhar a evolucao dele no mes. Ajudaria
// bastante como apoio na tomada de decisoes sobre as estrategias de publicacoes e campanhas"*. E,
// sobre como contar: *"exatamente este mapa, considerando os criterios do dia e multiplicando pelos
// dias do mes [...] vamos conseguir identificar a variacao de trafego das mensagens, se esta
// aumentando, estagnado ou diminuindo"*. Na previa ele pediu o DIA DA SEMANA embaixo de cada dia e
// LINHA no lugar das barras.
//
// ⛔ **Os quadros sao os do mapa do dia** (`SINAIS_MAPA`, mesmo rotulo e texto) e cada dia e' o mapa
// DAQUELE dia, calculado pela mesma consulta no servidor ('/contatos-mes'). O mes e' a soma:
// quem escreveu em 5 dias conta 5.
//
// ✅ O que a aba ANTIGA ensinou e continua valendo ([DA-692]/[DA-703]/[DA-704]):
//   1. **`sem movimento` nao e' zero.** De 13 a 31/08/2026 nada trafegou porque o numero estava
//      banido ([DA-537]) — a linha QUEBRA nesses dias em vez de despencar pra zero, e o eixo leva
//      hachura.
//   2. **Fim de semana marcado** — o dia da semana em negrito no sabado e domingo: o efeito do
//      calendario e' maior que o de muita acao.
//   3. **A regua sobe pra um numero REDONDO** acima do pico, senao o mesmo 20 desenha alturas
//      diferentes em meses diferentes.
//   4. **A altura do grafico e' MEDIDA** (ocupa o que sobra da janela) — ver o `useLayoutEffect`.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { SINAIS_MAPA, type SinalMapa } from './ContatosDoDia';

interface DiaMapa {
  dia: string;
  dia_util: boolean;
  hoje: boolean;
  sem_movimento: boolean;
  lista: number;
  escreveu: number;
  falamos: number;
  fly: number;
  sem_resposta: number;
  novo: number;
  apos_fly: number;
  comprou: number;
  vendido: number;
  /** Toda mensagem de fly que saiu no dia, SEM o filtro de "nao e' venda" do mapa. */
  fly_enviados: number;
}

/**
 * ⚠️ Quadro SO' do mes (pedido dele, 16/10/2026): conta ENVIOS, nao contatos do mapa — por isso nao
 * mora no `SINAIS_MAPA`, que e' o mapa do dia com espelho no servidor. "Receberam fly" tira quem
 * HOJE carrega NAO QUERO e afins (deu 125 no dia em que sairam 128); este nao tira ninguem.
 */
type Serie = SinalMapa | 'fly_enviados';
interface Quadro { chave: Serie; rotulo: string; explica: string }
const QUADRO_FLY_ENVIADOS: Quadro = {
  chave: 'fly_enviados',
  rotulo: 'Total de Flys Enviados por dia',
  explica: 'Todo fly que saiu no dia, sem tirar ninguem (inclusive quem depois pediu pra nao receber).',
};

const RESERVA_ABAIXO = 12;
function respiroDeBaixo(el: HTMLElement): number {
  const cs = getComputedStyle(el);
  return (parseFloat(cs.paddingBottom) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
}
const ALTURA_MIN = 110;
const ALTURA_MAX = 420;

// ⚠️ O rotulo do dia sai da STRING `YYYY-MM-DD`, nunca de `new Date(dia)`: a data pura lida como
// ISO vira meia-noite UTC e volta um dia atras no fuso da loja.
const numeroDoDia = (dia: string) => dia.slice(8, 10);
const SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'];
function diaDaSemana(dia: string): string {
  const [a, m, d] = dia.slice(0, 10).split('-').map(Number);
  return SEMANA[new Date(Date.UTC(a!, m! - 1, d!)).getUTCDay()] ?? '';
}
const fmtBRL = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** No mes o texto do quadro fala do DIA, nao de "hoje" — o mesmo texto do mapa do dia, trocado. */
const rotuloMes = (r: string) => r.replace(/ hoje$/, '');
const explicaMes = (e: string) => e.replace(/\bhoje\b/g, 'no dia');

export function EstatisticasContatos({ mes, ano, sinalRecarga }: { mes: number; ano: number; sinalRecarga?: number }) {
  const [dias, setDias] = useState<DiaMapa[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [serie, setSerie] = useState<Serie>('escreveu');
  /** Dia clicado no grafico: os quadros passam a mostrar AQUELE dia. `null` = o mes. */
  const [diaEscolhido, setDiaEscolhido] = useState<string | null>(null);
  const [alturaPlot, setAlturaPlot] = useState(180);
  const [larguraPlot, setLarguraPlot] = useState(0);
  const plotRef = useRef<HTMLDivElement>(null);
  const rodapeRef = useRef<HTMLParagraphElement>(null);
  const toast = useToast();

  const carregar = useCallback(async () => {
    setCarregando(true);
    setDiaEscolhido(null);
    try {
      const alvo = `${ano}-${String(mes).padStart(2, '0')}`;
      const r = await api.get<{ dias: DiaMapa[] }>('/api/fila-retomada/contatos-mes', { query: { mes: alvo } });
      setDias(r.dias);
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao carregar o mapa do mes');
    } finally {
      setCarregando(false);
    }
    // ⛔ `sinalRecarga` nas dependencias: e' o que faz o Atualizar da PAGINA alcancar esta aba, que
    // carrega sozinha ([DA-704], o mesmo mecanismo da Agenda em [DA-684]).
  }, [ano, mes, sinalRecarga, toast]);

  useEffect(() => { void carregar(); }, [carregar]);

  /**
   * ⛔ **A altura do grafico ocupa o que SOBRA da janela** ([DA-704], pedido dele com um retangulo
   * sobre a area vazia). `fundo da area rolavel − topo do plot − o que vem depois dele`: os dois
   * termos medidos independem da altura atual, entao a conta converge numa passada. A regua e' o
   * `main.app-content` (quem rola), descontado o padding dele, que conta no `scrollHeight` e nao
   * aparece no retangulo. A LARGURA sai da mesma medida — o SVG precisa dela em pixels.
   */
  useLayoutEffect(() => {
    if (carregando) return;
    let agendado = 0;
    const areaRolavel = (() => {
      let el = plotRef.current?.parentElement ?? null;
      while (el) {
        if (/(auto|scroll)/.test(getComputedStyle(el).overflowY)) return el;
        el = el.parentElement;
      }
      return null;
    })();
    const medir = () => {
      agendado = 0;
      const plot = plotRef.current;
      const rodape = rodapeRef.current;
      if (!plot || !rodape) return;
      const rp = plot.getBoundingClientRect();
      let fundo: HTMLElement = rodape;
      if (areaRolavel) {
        while (fundo.parentElement && fundo.parentElement !== areaRolavel) fundo = fundo.parentElement;
      }
      const depois = fundo.getBoundingClientRect().bottom - rp.bottom;
      const limite = areaRolavel
        ? areaRolavel.getBoundingClientRect().bottom - respiroDeBaixo(areaRolavel)
        : window.innerHeight;
      const alvo = Math.round(limite - RESERVA_ABAIXO - rp.top - depois);
      const limitado = Math.max(ALTURA_MIN, Math.min(ALTURA_MAX, alvo));
      setAlturaPlot((atual) => (Math.abs(atual - limitado) > 1 ? limitado : atual));
      setLarguraPlot((atual) => (Math.abs(atual - rp.width) > 1 ? Math.round(rp.width) : atual));
    };
    const agendar = () => { if (!agendado) agendado = requestAnimationFrame(medir); };
    medir();
    window.addEventListener('resize', agendar);
    const observador = new ResizeObserver(agendar);
    if (areaRolavel) observador.observe(areaRolavel);
    observador.observe(document.body);
    return () => {
      if (agendado) cancelAnimationFrame(agendado);
      window.removeEventListener('resize', agendar);
      observador.disconnect();
    };
  }, [carregando, dias]);

  const total = useMemo(() => {
    const t: Record<string, number> = {};
    for (const k of ['lista', 'escreveu', 'falamos', 'fly', 'sem_resposta', 'novo', 'apos_fly', 'comprou', 'vendido', 'fly_enviados'] as const) {
      t[k] = dias.reduce((s, d) => s + (Number(d[k]) || 0), 0);
    }
    return t;
  }, [dias]);

  const doDia = diaEscolhido ? dias.find((d) => d.dia === diaEscolhido) ?? null : null;
  const numeros: Record<string, number> = doDia ? (doDia as unknown as Record<string, number>) : total;

  const escala = useMemo(() => {
    const pico = Math.max(1, ...dias.map((d) => d[serie]));
    // Regua num numero REDONDO acima do pico: passo de 5 ate 30, de 10 ate 100, de 20 dai pra cima.
    const passo = pico <= 30 ? 5 : pico <= 100 ? 10 : 20;
    const teto = Math.max(passo, Math.ceil(pico / passo) * passo);
    const marcas: number[] = [];
    for (let v = teto; v > 0; v -= passo) marcas.push(v);
    return { teto, marcas };
  }, [dias, serie]);

  const quadro = (s: Quadro) => {
    const n = numeros[s.chave] ?? 0;
    const noGrafico = serie === s.chave;
    return (
      <button
        key={s.chave}
        type="button"
        title="Mostrar este quadro no grafico"
        onClick={() => setSerie(s.chave)}
        className="rounded-lg border text-left px-3 py-2"
        style={{
          backgroundColor: 'var(--panel-bg)',
          borderColor: noGrafico ? '#f59e0b' : 'var(--border-color, #cbd5e1)',
          borderWidth: noGrafico ? 3 : 1,
        }}
      >
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold tabular-nums" style={{ color: 'var(--num-destaque)' }}>{n}</span>
          <span className="font-semibold text-sm">{rotuloMes(s.rotulo)}</span>
        </div>
        {s.chave === 'comprou' && (
          <div className="text-sm font-semibold tabular-nums">R$ {fmtBRL(numeros.vendido ?? 0)}</div>
        )}
        <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{explicaMes(s.explica)}</div>
        {noGrafico && (
          <span className="inline-block mt-1 rounded px-1.5 py-0.5 text-[10px] font-bold" style={{ backgroundColor: '#fde68a', color: '#000' }}>
            NO GRAFICO
          </span>
        )}
      </button>
    );
  };

  // Geometria do SVG. Cada dia tem uma COLUNA de largura igual — o eixo de baixo usa as mesmas
  // colunas (flex:1), e e' isso que mantem o ponto em cima do rotulo do seu dia.
  // ⛔ So' o dia UTIL sem movimento quebra a linha (caixa fora do ar). Domingo sem mensagem e' a loja
  // fechada: zero de verdade, e quebrar ali partiu setembro em tres pedacos na primeira publicacao.
  const quebra = (d: DiaMapa) => d.sem_movimento && d.dia_util;
  const W = larguraPlot;
  const H = alturaPlot;
  const TOPO = 18; // reserva pro numero do ponto mais alto
  const n = dias.length || 1;
  const colW = W / n;
  const x = (i: number) => colW * i + colW / 2;
  const y = (v: number) => TOPO + (H - TOPO - 4) * (1 - v / escala.teto);
  // ⛔ A linha QUEBRA no dia sem movimento: ligar 12 de agosto a 1 de setembro por cima do banimento
  // desenharia uma tendencia que ninguem viveu.
  const trechos: string[] = [];
  let atual: string[] = [];
  dias.forEach((d, i) => {
    if (quebra(d)) { if (atual.length) trechos.push(atual.join(' ')); atual = []; return; }
    atual.push(`${x(i).toFixed(1)},${y(d[serie]).toFixed(1)}`);
  });
  if (atual.length) trechos.push(atual.join(' '));

  const semMovUteis = dias.filter((d) => d.sem_movimento && d.dia_util).length;
  const nomeSerie = serie === 'fly_enviados'
    ? 'Flys enviados'
    : rotuloMes(SINAIS_MAPA.find((s) => s.chave === serie)?.rotulo ?? '');

  return (
    <div>
      {carregando ? (
        <div className="text-center text-gray-400 py-12 text-sm">Calculando o mes — cada dia e o mapa daquele dia...</div>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap mb-1 text-sm">
            {doDia ? (
              <>
                <span className="rounded px-2 py-0.5 font-semibold" style={{ backgroundColor: '#fde68a', color: '#000' }}>
                  Mostrando o dia {numeroDoDia(doDia.dia)}/{doDia.dia.slice(5, 7)} ({diaDaSemana(doDia.dia)})
                </span>
                <button
                  type="button"
                  className="ml-2 inline-flex items-center gap-1 rounded px-3 py-1 text-[12px] font-semibold bg-blue-600 text-white hover:bg-blue-700"
                  style={{ whiteSpace: 'nowrap' }}
                  onClick={() => setDiaEscolhido(null)}
                >
                  Voltar ao mes
                </button>
              </>
            ) : (
              <span>
                <strong className="tabular-nums" style={{ color: 'var(--num-destaque)' }}>{total.lista}</strong>{' '}
                contatos-dia no mes · cada dia contado com os criterios do mapa do dia · clique num dia do grafico pra ver so ele
              </span>
            )}
          </div>

          <div className="text-xs font-bold uppercase mb-1" style={{ color: 'var(--text-muted)' }}>O movimento</div>
          {/* O quarto quadro na ponta direita da linha, ao lado de "Receberam fly" (retangulo dele no print). */}
          <div className="grid gap-2 mb-2" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
            {SINAIS_MAPA.filter((s) => s.grupo === 'movimento').map(quadro)}
            {quadro(QUADRO_FLY_ENVIADOS)}
          </div>
          <div className="text-xs font-bold uppercase mb-1" style={{ color: 'var(--text-muted)' }}>Onde agir</div>
          <div className="grid gap-2 mb-2" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
            {SINAIS_MAPA.filter((s) => s.grupo === 'acao').map(quadro)}
          </div>

          <div className="rounded-xl border border-gray-200 px-3 py-2" style={{ backgroundColor: 'var(--panel-bg)' }}>
            <div className="text-sm font-semibold mb-1" style={{ color: 'var(--panel-fg)' }}>
              {nomeSerie} — dia a dia
            </div>
            <div className="flex" style={{ gap: 6 }}>
              <div className="relative flex-shrink-0 tabular-nums"
                   style={{ height: alturaPlot, width: 26, fontSize: 11, color: 'var(--panel-fg)', opacity: 0.75 }}>
                {[...escala.marcas, 0].map((m) => (
                  <span key={m} className="absolute right-0" style={{ top: y(m) - 6, lineHeight: 1 }}>{m}</span>
                ))}
              </div>
              <div ref={plotRef} className="flex-1 relative" style={{ height: alturaPlot }}>
                {W > 0 && (
                  <svg width={W} height={H} style={{ display: 'block', overflow: 'visible' }}>
                    {escala.marcas.map((m) => (
                      <line key={m} x1={0} x2={W} y1={y(m)} y2={y(m)}
                            stroke="var(--input-border, #d1d5db)" strokeDasharray="4 4" opacity={0.6} />
                    ))}
                    <line x1={0} x2={W} y1={y(0)} y2={y(0)} stroke="var(--panel-fg)" strokeWidth={1.5} opacity={0.55} />
                    {doDia && (() => {
                      const i = dias.findIndex((d) => d.dia === doDia.dia);
                      return <rect x={colW * i} y={0} width={colW} height={H} fill="#fde68a" opacity={0.55} />;
                    })()}
                    {trechos.map((p, k) => (
                      <polyline key={k} points={p} fill="none" stroke="var(--primary)" strokeWidth={2.5} strokeLinejoin="round" />
                    ))}
                    {dias.map((d, i) => quebra(d) ? null : (
                      <g key={d.dia}>
                        <circle cx={x(i)} cy={y(d[serie])} r={4} fill="var(--primary)" stroke="#fff" strokeWidth={1.5} />
                        {/* O numero em tinta de TEXTO, nunca na cor da serie ([DA-703]). */}
                        <text x={x(i)} y={y(d[serie]) - 8} textAnchor="middle" fontSize={11} fontWeight={600}
                              fill="var(--panel-fg)">{d[serie]}</text>
                      </g>
                    ))}
                    {/* A coluna inteira do dia e' clicavel — mirar um ponto de 8px e' hostil. */}
                    {dias.map((d, i) => (
                      <rect key={`c${d.dia}`} x={colW * i} y={0} width={colW} height={H} fill="transparent"
                            style={{ cursor: quebra(d) ? 'default' : 'pointer' }}
                            onClick={() => { if (!quebra(d)) setDiaEscolhido(d.dia === diaEscolhido ? null : d.dia); }}>
                        <title>{quebra(d)
                          ? `${numeroDoDia(d.dia)} — sem movimento na caixa`
                          : `${numeroDoDia(d.dia)} (${diaDaSemana(d.dia)}) — ${d[serie]} · clique pra ver o mapa deste dia`}</title>
                      </rect>
                    ))}
                  </svg>
                )}
              </div>
            </div>

            {/* Eixo: dia, dia da semana (pedido dele na previa) e os flys do dia. As colunas repetem
                o recuo da regua, senao saem meia coluna fora do ponto. */}
            <div className="flex" style={{ gap: 6 }}>
              <div className="flex-shrink-0" style={{ width: 26 }} />
              <div className="flex-1 flex">
                {dias.map((d) => (
                  <div key={d.dia} className="flex-1 flex flex-col items-center leading-tight min-w-0"
                       style={{ cursor: quebra(d) ? 'default' : 'pointer' }}
                       onClick={() => { if (!quebra(d)) setDiaEscolhido(d.dia === diaEscolhido ? null : d.dia); }}>
                    {d.sem_movimento && d.dia_util ? (
                      <div className="w-full" style={{
                        height: 5, marginTop: 1,
                        backgroundImage: 'repeating-linear-gradient(45deg, #f97316 0 3px, transparent 3px 6px)',
                      }} />
                    ) : <div style={{ height: 3 }} />}
                    <span className="tabular-nums" style={{ fontSize: 11, color: 'var(--panel-fg)', fontWeight: 600 }}>
                      {numeroDoDia(d.dia)}
                    </span>
                    {/* Fim de semana em NEGRITO — sinal que nao depende de cor ([[user_daltonismo]]). */}
                    <span style={{ fontSize: 10, color: 'var(--panel-fg)', fontWeight: d.dia_util ? 400 : 700 }}>
                      {diaDaSemana(d.dia)}
                    </span>
                    <span style={{ fontSize: 10, height: 15, marginTop: 1 }}>
                      {d.fly > 0 && (
                        <span className="rounded px-1 tabular-nums" style={{ backgroundColor: '#fde68a', color: '#000', whiteSpace: 'nowrap' }}>
                          {d.fly} fly
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p ref={rodapeRef} className="mt-2" style={{ fontSize: 13, color: 'var(--panel-fg)' }}>
            Cada dia e o <strong>mapa daquele dia</strong>, com os mesmos criterios da aba "Contatos do dia"; o mes e a
            soma dos dias (quem escreveu em 5 dias conta 5). A etiqueta amarela embaixo do dia e quantos contatos
            receberam fly. Os dias passados sao recalculados com as regras e os cadastros de hoje.
            {semMovUteis > 0 && (
              <> <strong>{semMovUteis} {semMovUteis === 1 ? 'dia util' : 'dias uteis'} sem nenhuma mensagem na caixa</strong> (hachura
              laranja): a linha quebra ali porque nao houve movimento, e nao porque ninguem procurou a loja.</>
            )}
          </p>
        </>
      )}
    </div>
  );
}
